import React, { useState } from 'react';
import { ShieldCheck, ArrowUpRight, Calendar, Mail, Target } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

export const EXEC_ACCENTS = ['#00E5FF', '#8A2BE2', '#F59E0B', '#10B981', '#F43F5E', '#38BDF8'];
export const TAG_HEX = { violet: '#A78BFA', red: '#F87171', amber: '#FBBF24' };
export const initialsOf = (n) => (n || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();

export const ExecAvatar = ({ member, className, hex }) => {
  const [failed, setFailed] = useState(false);
  return member.avatar && !failed
    ? <img src={getImageUrl(member.avatar)} alt={member.name} className={`${className} object-cover`} onError={() => setFailed(true)} />
    : <div className={`${className} flex items-center justify-center`} style={{ background: `radial-gradient(circle at 30% 25%, ${hex}55, #0b0b16 70%)` }}>
        <span className="text-5xl font-light text-white/40">{initialsOf(member.name)}</span>
      </div>;
};

export const ExecutiveCard = ({ member, index, onOpen }) => {
  const hex = EXEC_ACCENTS[index % EXEC_ACCENTS.length];
  return (
    <button type="button" onClick={() => onOpen(index)} data-testid={`executive-card-${member.id}`}
      style={{ animation: `fadeInUp 0.55s ease-out ${0.08 * index}s both` }}
      className="group relative text-left overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0A0A12]/80 backdrop-blur-md transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-white/[0.18] hover:shadow-[0_30px_60px_-30px_rgba(0,229,255,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/50">
      <div className="relative h-72 overflow-hidden">
        <ExecAvatar member={member} hex={hex} className="w-full h-full transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] via-[#0A0A12]/30 to-transparent" />
        <div className="pointer-events-none absolute -bottom-16 -right-10 w-48 h-48 rounded-full blur-[80px] opacity-40 group-hover:opacity-80 transition-opacity duration-700" style={{ background: hex }} />
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.18em] uppercase backdrop-blur-md border"
          style={{ background: `${hex}1f`, borderColor: `${hex}55`, color: hex }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: hex, boxShadow: `0 0 8px ${hex}` }} />{member.roleShort || 'Executive'}
        </span>
        <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/15 flex items-center justify-center text-white/70 group-hover:text-white group-hover:rotate-45 transition-transform duration-500">
          <ArrowUpRight className="w-4 h-4" />
        </span>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-2xl font-semibold text-white tracking-tight leading-tight">{member.name}</h3>
          <p className="text-[11px] tracking-[0.12em] uppercase mt-1" style={{ color: `${hex}cc` }}>{member.role}</p>
        </div>
      </div>
      <div className="relative p-5 pt-3 space-y-3">
        {member.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {member.tags.map((t, i) => {
              const th = TAG_HEX[t.color] || '#00E5FF';
              return <span key={i} data-testid={`exec-tag-${member.id}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.08em] uppercase border"
                style={{ background: `${th}14`, borderColor: `${th}40`, color: th }}><ShieldCheck className="w-2.5 h-2.5" />{t.label}</span>;
            })}
          </div>
        )}
        <p className="text-sm text-white/50 leading-relaxed line-clamp-2 min-h-[40px]">{member.bio}</p>
        {member.focusAreas?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {member.focusAreas.slice(0, 3).map((a, i) => <span key={i} className="px-2 py-0.5 rounded-md text-[10px] bg-white/[0.04] border border-white/[0.07] text-white/55">{a}</span>)}
            {member.focusAreas.length > 3 && <span className="px-2 py-0.5 text-[10px] text-white/35">+{member.focusAreas.length - 3}</span>}
          </div>
        )}
        <div className="pt-3 border-t border-white/[0.05] flex items-center justify-between text-[10px] text-white/40">
          <span className="flex items-center gap-3">
            {member.joinedYear && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Since {member.joinedYear}</span>}
            {member.focusAreas?.length > 0 && <span className="flex items-center gap-1"><Target className="w-3 h-3" />{member.focusAreas.length} focus areas</span>}
            {member.contact?.email && <Mail className="w-3 h-3" />}
          </span>
          <span className="tracking-[0.14em] uppercase font-semibold text-white/60 group-hover:text-white transition-colors">View profile</span>
        </div>
      </div>
      <div className="absolute bottom-0 left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(90deg, transparent, ${hex}99, transparent)` }} />
    </button>
  );
};
